import { getWeekStart, parseEnGbDate } from './dateUtils';
import { getExerciseMuscles, MUSCLE_KEYS } from './exercises';
import { isSetLogged } from './workoutUtils';

const PRIMARY_WEIGHT = 1;
const SECONDARY_WEIGHT = 0.5;

export function setVolume(set) {
  const reps = parseFloat(set?.reps);
  const weight = parseFloat(set?.weight);
  if (!Number.isFinite(reps) || !Number.isFinite(weight)) return 0;
  return reps * weight;
}

export function workoutVolume(workout) {
  return (workout?.exercises || []).reduce((sum, ex) => {
    if (ex.trackingType === 'timed') return sum;
    return sum + (ex.sets || []).filter(isSetLogged).reduce((s, set) => s + setVolume(set), 0);
  }, 0);
}

export function addMuscleSets(target, muscles, amount) {
  (muscles || []).forEach((key) => {
    if (!key) return;
    target[key] = (target[key] || 0) + amount;
  });
}

export function muscleSetsFromWorkout(workout) {
  const counts = {};
  (workout?.exercises || []).forEach((ex) => {
    const logged = (ex.sets || []).filter(isSetLogged).length;
    if (!logged) return;
    const { primary, secondary } = getExerciseMuscles(ex.name, ex.primaryMuscle);
    if (!primary.length && !secondary.length) {
      counts.unassigned = (counts.unassigned || 0) + logged;
      return;
    }
    addMuscleSets(counts, primary, logged * PRIMARY_WEIGHT);
    addMuscleSets(counts, secondary, logged * SECONDARY_WEIGHT);
  });
  return counts;
}

export function mergeMuscleSets(into, from) {
  Object.entries(from || {}).forEach(([key, value]) => {
    into[key] = (into[key] || 0) + value;
  });
  return into;
}

export function muscleSetsInRange(workoutLog, days) {
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - (days - 1));
  const counts = {};
  Object.entries(workoutLog || {}).forEach(([date, workout]) => {
    const d = parseEnGbDate(date);
    if (!d || d < cutoff) return;
    mergeMuscleSets(counts, muscleSetsFromWorkout(workout));
  });
  return counts;
}

export function volumeInRange(workoutLog, fromDate, toDate) {
  let total = 0;
  Object.entries(workoutLog || {}).forEach(([date, workout]) => {
    const d = parseEnGbDate(date);
    if (!d) return;
    if (fromDate && d < fromDate) return;
    if (toDate && d > toDate) return;
    total += workoutVolume(workout);
  });
  return total;
}

export function weeklyVolumeSummary(workoutLog, now = new Date()) {
  const thisStart = getWeekStart(now);
  const lastStart = new Date(thisStart);
  lastStart.setDate(lastStart.getDate() - 7);
  const lastEnd = new Date(thisStart);
  lastEnd.setDate(lastEnd.getDate() - 1);
  lastEnd.setHours(23, 59, 59, 999);

  const thisWeek = volumeInRange(workoutLog, thisStart, now);
  const lastWeek = volumeInRange(workoutLog, lastStart, lastEnd);
  let changePct = null;
  if (lastWeek > 0) {
    changePct = ((thisWeek - lastWeek) / lastWeek) * 100;
  } else if (thisWeek > 0) {
    changePct = 100;
  }
  return { thisWeek, lastWeek, changePct };
}

export function muscleSetsThisWeek(workoutLog, now = new Date()) {
  const start = getWeekStart(now);
  const counts = {};
  Object.entries(workoutLog || {}).forEach(([date, workout]) => {
    const d = parseEnGbDate(date);
    if (!d || d < start) return;
    mergeMuscleSets(counts, muscleSetsFromWorkout(workout));
  });
  return counts;
}

export function muscleBarData(counts, language) {
  return MUSCLE_KEYS
    .map((key) => ({ key, value: Math.round((counts[key] || 0) * 10) / 10 }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);
}

export function workoutsCompletedInRange(workoutLog, days) {
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - (days - 1));
  let n = 0;
  Object.entries(workoutLog || {}).forEach(([date, workout]) => {
    const d = parseEnGbDate(date);
    if (!d || d < cutoff) return;
    if ((workout?.exercises || []).some((ex) => (ex.sets || []).some(isSetLogged))) n += 1;
  });
  return n;
}

export function setsLoggedInRange(workoutLog, days) {
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - (days - 1));
  let n = 0;
  Object.entries(workoutLog || {}).forEach(([date, workout]) => {
    const d = parseEnGbDate(date);
    if (!d || d < cutoff) return;
    (workout?.exercises || []).forEach((ex) => {
      n += (ex.sets || []).filter(isSetLogged).length;
    });
  });
  return n;
}

export { PRIMARY_WEIGHT, SECONDARY_WEIGHT };
